import type { TurnWorld, Unit, Building, Domain, BattleScript } from './types';
import { UNIT_DEFS, resolveBaseType } from './units';
import { getRecruitList } from './factions';
import { getMovementRange, findPath, parseCoordKey } from './pathfinding';
import type { Coord } from './pathfinding';
import { canAttack, buildBattleScript, manhattanDistance } from './combat';
import { canCapture, executeCapture } from './capture';
import { startTraining, currentPlayerId } from './turnEngine';
import { getMovementCost } from './terrain';
import { canLoadInto, canUnload, getUnloadTiles, executeUnload } from './cargo';
import { computePlayerVisible, computeUnitVision, updateExplored } from './vision';
import { computeZocTiles } from './zoc';
import { computeHealTargets, executeHeal, MEDIC_HEAL_TYPES } from './healing';
import type { HealResult } from './healing';
import { computeResupplyTargets, executeResupply, resolveResupplyDomain } from './supply';
import { canUseAbility, executeAirStrike } from './abilities';
import type { ResupplyResult } from './supply';

/** Result of a single AI unit action — for camera/animation in scene. */
export interface AiUnitResult {
  unitId: number;
  fromCol: number;
  fromRow: number;
  toCol: number;
  toRow: number;
  path: Coord[];
  captured: boolean;
  killed: boolean;
  healResults?: HealResult[];
  resupplyResults?: ResupplyResult[];
  /** Set when this action was an attack — drives the full-screen battle scene. */
  battle?: BattleScript;
}

/** Module-level tracking — set by aiActUnit internals, read by aiActNextUnit. */
let _lastPath: Coord[] = [];
let _lastCaptured = false;
let _lastHealResults: HealResult[] | undefined;
let _lastResupplyResults: ResupplyResult[] | undefined;
let _lastBattle: BattleScript | undefined;
/** AI fog of war — visible tiles for the current AI player. */
let _aiVisible = new Set<string>();

/**
 * Execute a complete AI turn for the current player (instant mode).
 * Priority: capture → attack → advance toward objectives.
 */
export function runAiTurn(world: TurnWorld): void {
  const pid = currentPlayerId(world);
  _aiVisible = computePlayerVisible(pid, world);
  updateExplored(pid, _aiVisible, world);
  // eslint-disable-next-line no-empty
  while (aiActNextUnit(world) !== null) {}
  aiRecruitAll(world);
}

/** Execute the next unacted AI unit's turn. Returns null when all units have acted. */
export function aiActNextUnit(world: TurnWorld): AiUnitResult | null {
  const pid = currentPlayerId(world);
  // Hard guard: never act on the human player's units
  if (pid === world.humanPlayer) return null;
  const unit = [...world.units.values()].find((u) => u.owner === pid && !u.acted);
  if (!unit || !world.units.has(unit.id)) return null;

  const fromCol = unit.col;
  const fromRow = unit.row;
  _lastPath = [];
  _lastCaptured = false;
  _lastHealResults = undefined;
  _lastResupplyResults = undefined;
  _lastBattle = undefined;
  // Ensure AI visibility is computed (for step-by-step mode)
  if (_aiVisible.size === 0) {
    _aiVisible = computePlayerVisible(pid, world);
    updateExplored(pid, _aiVisible, world);
  }
  aiActUnit(world, unit, pid);

  // Incremental visibility update after unit acts.
  // Instead of recomputing BFS for ALL units (O(units × sightRange²)),
  // only compute the moved unit's new vision and add it (conservative: AI sees ≥ actual).
  // This is safe because AI never needs to "un-see" tiles within a single turn.
  if (unit.col !== fromCol || unit.row !== fromRow) {
    if (world.units.has(unit.id)) {
      const newVision = computeUnitVision(unit, world.map);
      for (const key of newVision) _aiVisible.add(key);
    }
  }
  updateExplored(pid, _aiVisible, world);

  // unit.col/row reflect post-move position even if unit died from counter-attack
  const toCol = unit.col;
  const toRow = unit.row;
  const killed = !world.units.has(unit.id);
  return {
    unitId: unit.id,
    fromCol, fromRow,
    toCol, toRow,
    path: _lastPath,
    captured: _lastCaptured,
    killed,
    healResults: _lastHealResults,
    resupplyResults: _lastResupplyResults,
    battle: _lastBattle,
  };
}

/** Recruit at all owned buildings (end-of-turn phase). */
export function aiRecruitAll(world: TurnWorld): void {
  const pid = currentPlayerId(world);
  // Hard guard: never recruit for the human player
  if (pid === world.humanPlayer) return;
  aiRecruit(world, pid);
}

/** Best tile to centre an air-strike: most visible enemies in the 3×3, zero own. */
function bestAirStrikeTile(world: TurnWorld, pid: number): { col: number; row: number } | null {
  let best: { col: number; row: number; score: number } | null = null;
  for (const [, e] of world.units) {
    if (e.owner === pid) continue;
    if (!_aiVisible.has(`${e.col},${e.row}`)) continue;
    let enemies = 0, own = 0;
    for (const [, u] of world.units) {
      if (Math.abs(u.col - e.col) <= 1 && Math.abs(u.row - e.row) <= 1) {
        if (u.owner === pid) own++; else enemies++;
      }
    }
    if (own > 0 || enemies < 2) continue;
    if (!best || enemies > best.score) best = { col: e.col, row: e.row, score: enemies };
  }
  return best ? { col: best.col, row: best.row } : null;
}

/** AI for commanders: officer air-strike on a good cluster, else trail the
 *  friendly group to keep allies inside the aura. Never charges into melee. */
function aiActCommander(world: TurnWorld, unit: Unit, range: Map<string, number>, pid: number): void {
  const def = UNIT_DEFS[unit.type];
  const ability = def.abilities?.find((a) => a.effect === 'air-strike');
  if (ability && canUseAbility(unit, ability.id) && world.players[pid].gold >= ability.cost) {
    const target = bestAirStrikeTile(world, pid);
    if (target) {
      world.players[pid].gold -= ability.cost;
      executeAirStrike(world, unit, target.col, target.row);
      return;
    }
  }
  // Trail the nearest friendly combat unit (stay close for aura coverage).
  let nearest: Unit | null = null;
  let bestD = Infinity;
  for (const [, u] of world.units) {
    if (u.owner !== pid || u.id === unit.id) continue;
    if (u.type === 'officer' || u.type.startsWith('hero-')) continue;
    const d = chebyshevDist(unit, u);
    if (d < bestD) { bestD = d; nearest = u; }
  }
  if (nearest && bestD > 1) moveToward(world, unit, range, nearest.col, nearest.row);
  unit.acted = true;
  unit.movePoints = 0;
}

function aiActUnit(world: TurnWorld, unit: Unit, pid: number): void {
  const zoc = computeZocTiles(world, unit, _aiVisible);
  const range = getMovementRange(world, unit, zoc);
  const unitDef = UNIT_DEFS[unit.type];

  // Medic-truck-specific logic
  if (unit.type === 'medic') {
    aiActMedic(world, unit, range, pid);
    return;
  }

  // Commanders (officer + heroes) — support, never charge into melee.
  if (unit.type === 'officer' || unit.type.startsWith('hero-')) {
    aiActCommander(world, unit, range, pid);
    return;
  }

  // Pure resupplier (no cargo — e.g. supply-truck): dedicated resupply routine.
  const hasCargo = !!unitDef.cargoCapacity && unitDef.cargoCapacity > 0;
  if (resolveResupplyDomain(unit.type) !== null && !hasCargo) {
    aiActResupplier(world, unit, range, pid);
    return;
  }

  // Cargo carriers (APC, ship/plane transports, aircraft carrier). Carriers and
  // transports that are also resuppliers refuel opportunistically inside aiActApc.
  if (hasCargo) {
    aiActApc(world, unit, range, pid);
    return;
  }

  // Priority 1: Capture — if capture-capable unit on/near a capturable building, go capture
  {
    const capturable = canCapture(world, unit);
    if (capturable) {
      executeCapture(world, unit, capturable);
      _lastCaptured = true;
      return;
    }

    // Find nearest capturable building and move toward it
    const capTarget = findNearestCapturableBuilding(world, unit, pid);
    if (capTarget) {
      const moved = moveToward(world, unit, range, capTarget.col, capTarget.row);
      if (moved) {
        // Check if we can capture after moving
        const cap = canCapture(world, unit);
        if (cap) {
          executeCapture(world, unit, cap);
          _lastCaptured = true;
          return;
        }
      }
    }
  }

  // Priority 2: Attack — find weakest enemy in attack range (or move-then-attack range)
  const attacked = aiTryAttack(world, unit, range);
  if (attacked) return;

  // Priority 3: Move toward nearest enemy or objective
  if (!unit.acted) {
    const target = findNearestEnemy(world, unit, pid);
    if (target) {
      moveToward(world, unit, range, target.col, target.row);
    }
    unit.acted = true;
    unit.movePoints = 0;
  }
}

/** AI logic for APC: load infantry → transport → unload near enemy buildings → blockade. */
function aiActApc(world: TurnWorld, apc: Unit, range: Map<string, number>, pid: number): void {
  // Carriers / transports that resupply (air→planes, water→ships) top up adjacent
  // matching-domain allies first — a free, high-value action before ferrying.
  if (resolveResupplyDomain(apc.type) !== null && computeResupplyTargets(world, apc.id).length > 0) {
    _lastResupplyResults = executeResupply(world, apc.id);
    return;
  }

  const hasCargo = (apc.cargo?.length ?? 0) > 0;

  if (hasCargo) {
    // Priority: ferry cargo to capture an enemy building.
    const capTarget = findNearestCapturableBuilding(world, apc, pid);
    if (capTarget) {
      if (canUnload(world, apc) && chebyshevDist(apc, capTarget) <= 1) {
        aiUnload(world, apc, capTarget);
      } else {
        moveToward(world, apc, range, capTarget.col, capTarget.row);
        if (canUnload(world, apc) && chebyshevDist(apc, capTarget) <= 1) {
          aiUnload(world, apc, capTarget);
        }
      }
    } else {
      // No building objective (e.g. a naval battle): advance with the fleet and
      // launch the cargo — carrier aircraft / landing craft — at the enemy once
      // within striking distance, so the AI uses its air power offensively.
      const enemy = findNearestEnemy(world, apc, pid);
      if (enemy) {
        const STRIKE_RANGE = 4;
        if (canUnload(world, apc) && chebyshevDist(apc, enemy) <= STRIKE_RANGE) {
          aiUnload(world, apc, enemy);
        } else {
          moveToward(world, apc, range, enemy.col, enemy.row);
          if (canUnload(world, apc) && chebyshevDist(apc, enemy) <= STRIKE_RANGE) {
            aiUnload(world, apc, enemy);
          }
        }
      }
    }
    apc.acted = true;
    apc.movePoints = 0;
    return;
  }

  // No cargo — find nearest friendly infantry to pick up
  let bestInf: Unit | null = null;
  let bestDist = Infinity;
  for (const [, u] of world.units) {
    if (u.owner !== pid || u.type !== 'infantry') continue;
    if (!canLoadInto(u, apc)) continue;
    const dist = manhattanDistance(apc, u);
    if (dist < bestDist) {
      bestDist = dist;
      bestInf = u;
    }
  }

  if (bestInf) {
    // Move toward infantry
    moveToward(world, apc, range, bestInf.col, bestInf.row);
    // Check if we're now on the same tile as infantry (infantry moved onto us via pathfinding)
    // The actual loading happens when infantry walks onto APC, not when APC walks onto infantry
  }

  apc.acted = true;
  apc.movePoints = 0;
}

/** Chebyshev distance (king-move). */
function chebyshevDist(a: { col: number; row: number }, b: { col: number; row: number }): number {
  return Math.max(Math.abs(a.col - b.col), Math.abs(a.row - b.row));
}

/** Compute healScore for a given tile: sum of (maxHp - hp) for nearby healable wounded allies. */
function healScore(world: TurnWorld, col: number, row: number, pid: number): number {
  let score = 0;
  for (const [, u] of world.units) {
    if (u.owner !== pid) continue;
    if (!MEDIC_HEAL_TYPES.has(u.type)) continue;
    if (u.hp >= UNIT_DEFS[u.type].maxHp) continue;
    if (chebyshevDist({ col, row }, u) > 1) continue;
    score += UNIT_DEFS[u.type].maxHp - u.hp;
  }
  return score;
}

/** AI logic for medic-truck: heal adjacent wounded → move toward best healScore → follow cluster. */
function aiActMedic(world: TurnWorld, medic: Unit, range: Map<string, number>, pid: number): void {
  // Priority 1: Heal adjacent wounded allies
  const targets = computeHealTargets(world, medic.id);
  if (targets.length > 0) {
    const results = executeHeal(world, medic.id);
    _lastHealResults = results;
    return;
  }

  // Priority 2: Move toward tile with highest healScore
  let bestKey: string | null = null;
  let bestScore = 0;

  for (const key of range.keys()) {
    const pos = parseCoordKey(key);
    // healScore at this tile — but exclude the medic itself from score (it'll be at this tile)
    const score = healScore(world, pos.col, pos.row, pid);
    if (score > bestScore) {
      bestScore = score;
      bestKey = key;
    }
  }

  if (bestKey && bestScore > 0) {
    const pos = parseCoordKey(bestKey);
    const path = findPath(world, medic, pos);
    if (path) {
      _lastPath = path;
      const def = UNIT_DEFS[medic.type];
      let cost = 0;
      for (const step of path) {
        const terrain = world.map.tiles[step.row][step.col].type;
        cost += getMovementCost(terrain, def.domain, medic.type);
      }
      medic.col = pos.col;
      medic.row = pos.row;
      medic.movePoints = Math.max(0, medic.movePoints - cost);
    }
    medic.acted = true;
    medic.movePoints = 0;
    return;
  }

  // Priority 3: Fallback — move toward largest friendly cluster
  let bestClusterTarget: { col: number; row: number } | null = null;
  let bestClusterSize = 0;
  const friendlyUnits = [...world.units.values()].filter(u => u.owner === pid && u.id !== medic.id);

  for (const u of friendlyUnits) {
    // Count friendlies within Chebyshev ≤ 2 of this unit (cluster density)
    let clusterSize = 0;
    for (const other of friendlyUnits) {
      if (chebyshevDist(u, other) <= 2) clusterSize++;
    }
    if (clusterSize > bestClusterSize) {
      bestClusterSize = clusterSize;
      bestClusterTarget = { col: u.col, row: u.row };
    }
  }

  if (bestClusterTarget) {
    moveToward(world, medic, range, bestClusterTarget.col, bestClusterTarget.row);
  }

  medic.acted = true;
  medic.movePoints = 0;
}

/** Compute supplyScore for a tile: sum of (maxSupply - supply) for adjacent friendly units needing supply. */
function supplyScore(world: TurnWorld, col: number, row: number, pid: number, domain: Domain): number {
  let score = 0;
  for (const [, u] of world.units) {
    if (u.owner !== pid) continue;
    const def = UNIT_DEFS[u.type];
    if (def.domain !== domain) continue; // resupplier only helps its own domain
    const capacity = def.maxFuel + def.maxAmmo;
    if (capacity === 0) continue;
    const current = u.fuel + u.ammo;
    if (current >= capacity) continue;
    if (chebyshevDist({ col, row }, u) > 1) continue;
    score += capacity - current;
  }
  return score;
}

/** AI logic for any mobile resupplier (truck→land, carrier/plane-transport→air,
 *  ship-transport→water): resupply adjacent matching-domain allies → move toward
 *  the lowest-supply matching-domain ally → follow cluster. */
function aiActResupplier(world: TurnWorld, truck: Unit, range: Map<string, number>, pid: number): void {
  const domain = resolveResupplyDomain(truck.type);
  if (domain === null) return; // not a resupplier — defensive
  // Priority 1: Resupply adjacent allies (already domain-filtered)
  const targets = computeResupplyTargets(world, truck.id);
  if (targets.length > 0) {
    const results = executeResupply(world, truck.id);
    _lastResupplyResults = results;
    return;
  }

  // Priority 2: Move toward tile with highest supplyScore (matching domain)
  let bestKey: string | null = null;
  let bestScore = 0;

  for (const key of range.keys()) {
    const pos = parseCoordKey(key);
    const score = supplyScore(world, pos.col, pos.row, pid, domain);
    if (score > bestScore) {
      bestScore = score;
      bestKey = key;
    }
  }

  if (bestKey && bestScore > 0) {
    const pos = parseCoordKey(bestKey);
    const path = findPath(world, truck, pos);
    if (path) {
      _lastPath = path;
      const def = UNIT_DEFS[truck.type];
      let cost = 0;
      for (const step of path) {
        const terrain = world.map.tiles[step.row][step.col].type;
        cost += getMovementCost(terrain, def.domain, truck.type);
      }
      truck.col = pos.col;
      truck.row = pos.row;
      truck.movePoints = Math.max(0, truck.movePoints - cost);
    }
    truck.acted = true;
    truck.movePoints = 0;
    return;
  }

  // Priority 3: Move toward the matching-domain friendly unit with lowest combined supply ratio
  let lowestRatio = 1;
  let lowestSupplyTarget: { col: number; row: number } | null = null;
  for (const [, u] of world.units) {
    if (u.owner !== pid || u.id === truck.id) continue;
    const def = UNIT_DEFS[u.type];
    if (def.domain !== domain) continue;
    const capacity = def.maxFuel + def.maxAmmo;
    if (capacity === 0) continue;
    const current = u.fuel + u.ammo;
    const ratio = current / capacity;
    if (ratio < lowestRatio) {
      lowestRatio = ratio;
      lowestSupplyTarget = { col: u.col, row: u.row };
    }
  }

  if (lowestSupplyTarget && lowestRatio < 0.5) {
    moveToward(world, truck, range, lowestSupplyTarget.col, lowestSupplyTarget.row);
    truck.acted = true;
    truck.movePoints = 0;
    return;
  }

  // Priority 4: Fallback — move toward largest friendly cluster
  let bestClusterTarget: { col: number; row: number } | null = null;
  let bestClusterSize = 0;
  const friendlyUnits = [...world.units.values()].filter(u => u.owner === pid && u.id !== truck.id);

  for (const u of friendlyUnits) {
    let clusterSize = 0;
    for (const other of friendlyUnits) {
      if (chebyshevDist(u, other) <= 2) clusterSize++;
    }
    if (clusterSize > bestClusterSize) {
      bestClusterSize = clusterSize;
      bestClusterTarget = { col: u.col, row: u.row };
    }
  }

  if (bestClusterTarget) {
    moveToward(world, truck, range, bestClusterTarget.col, bestClusterTarget.row);
  }

  truck.acted = true;
  truck.movePoints = 0;
}

/** Unload cargo adjacent to a target building. */
function aiUnload(world: TurnWorld, apc: Unit, target: { col: number; row: number }): void {
  if (!apc.cargo || apc.cargo.length === 0) return;
  const tiles = getUnloadTiles(world, apc);
  // Prefer the unload tile closest to the target (a building to capture, or an
  // enemy to strike when launching carrier aircraft).
  let bestTile = tiles[0];
  let bestDist = Infinity;
  for (const t of tiles) {
    const dist = manhattanDistance(t, target);
    if (dist < bestDist) {
      bestDist = dist;
      bestTile = t;
    }
  }
  if (bestTile) {
    executeUnload(world, apc, bestTile.col, bestTile.row, 0);
  }
}

function aiTryAttack(world: TurnWorld, unit: Unit, range: Map<string, number>): boolean {
  const atkDef = UNIT_DEFS[unit.type];

  // Check direct attack from current position
  let bestTarget: Unit | null = null;
  let bestScore = -Infinity;

  for (const [, enemy] of world.units) {
    if (enemy.owner === unit.owner) continue;
    // Fog of war: only attack visible enemies
    if (!_aiVisible.has(`${enemy.col},${enemy.row}`)) continue;
    if (canAttack(unit, enemy)) {
      const score = scoreAttackTarget(enemy);
      if (score > bestScore) {
        bestScore = score;
        bestTarget = enemy;
      }
    }
  }

  if (bestTarget) {
    _lastBattle = buildBattleScript(world, unit, bestTarget);
    unit.acted = true;
    unit.movePoints = 0;
    return true;
  }

  // Check move-then-attack
  for (const [, enemy] of world.units) {
    if (enemy.owner === unit.owner) continue;
    // Fog of war: only attack visible enemies
    if (!_aiVisible.has(`${enemy.col},${enemy.row}`)) continue;

    // Find a reachable tile from which we can attack this enemy
    let bestPos: { col: number; row: number } | null = null;
    let bestPosScore = -Infinity;

    const minRange = atkDef.minAttackRange ?? 1;
    for (const key of range.keys()) {
      const pos = parseCoordKey(key);
      const dist = chebyshevDist(pos, enemy);
      if (dist >= minRange && dist <= atkDef.attackRange) {
        const s = scoreAttackTarget(enemy);
        if (s > bestPosScore) {
          bestPosScore = s;
          bestPos = pos;
          bestTarget = enemy;
        }
      }
    }

    if (bestPos && bestTarget) {
      const zocSet = computeZocTiles(world, unit, _aiVisible);
      const path = findPath(world, unit, bestPos, zocSet);
      if (path) {
        _lastPath = path;
        unit.col = bestPos.col;
        unit.row = bestPos.row;
        unit.movePoints = 0;
        _lastBattle = buildBattleScript(world, unit, bestTarget);
        unit.acted = true;
        return true;
      }
    }
  }

  return false;
}

/** Score an attack target — higher = more desirable. Prefer low-HP and high-value targets. */
function scoreAttackTarget(enemy: Unit): number {
  const def = UNIT_DEFS[enemy.type];
  return def.cost / enemy.hp; // value per HP — prioritize finishing weak expensive units
}

function findNearestCapturableBuilding(
  world: TurnWorld, unit: Unit, pid: number,
): Building | null {
  let best: Building | null = null;
  let bestDist = Infinity;
  const explored = world.explored[pid];

  for (const [, b] of world.buildings) {
    if (b.owner === pid) continue; // already ours
    // Fog of war: only target buildings the AI knows about (explored)
    if (!explored.has(`${b.col},${b.row}`)) continue;
    const dist = manhattanDistance(unit, b);
    if (dist < bestDist) {
      bestDist = dist;
      best = b;
    }
  }
  return best;
}

function findNearestEnemy(
  world: TurnWorld, unit: Unit, pid: number,
): { col: number; row: number } | null {
  let best: { col: number; row: number } | null = null;
  let bestDist = Infinity;
  const explored = world.explored[pid];

  // Prefer enemy buildings over units (strategic targets) — use explored (known positions)
  for (const [, b] of world.buildings) {
    if (b.owner !== null && b.owner !== pid) {
      if (!explored.has(`${b.col},${b.row}`)) continue;
      const dist = manhattanDistance(unit, b);
      if (dist < bestDist) {
        bestDist = dist;
        best = { col: b.col, row: b.row };
      }
    }
  }

  // Then enemy units — only visible ones
  for (const [, e] of world.units) {
    if (e.owner === pid) continue;
    if (!_aiVisible.has(`${e.col},${e.row}`)) continue;
    const dist = manhattanDistance(unit, e);
    if (dist < bestDist) {
      bestDist = dist;
      best = { col: e.col, row: e.row };
    }
  }

  // Fallback: if nothing is visible or known (large open maps with no buildings,
  // fleets starting out of sight), still head for the nearest enemy by its
  // actual position so the AI presses the attack instead of idling. Firing still
  // requires line of sight (see aiTryAttack), so this is only a movement heading,
  // not free fire.
  if (best === null) {
    for (const [, e] of world.units) {
      if (e.owner === pid) continue;
      const dist = manhattanDistance(unit, e);
      if (dist < bestDist) {
        bestDist = dist;
        best = { col: e.col, row: e.row };
      }
    }
  }

  return best;
}

/** Move unit as close to target as possible within movement range. Returns true if moved. */
function moveToward(
  world: TurnWorld, unit: Unit, range: Map<string, number>,
  targetCol: number, targetRow: number,
): boolean {
  // Compute ZoC for soft avoidance penalty (+0.5 for tiles in enemy ZoC)
  const zoc = computeZocTiles(world, unit, _aiVisible);
  let bestKey: string | null = null;
  let bestDist = manhattanDistance(unit, { col: targetCol, row: targetRow });

  for (const key of range.keys()) {
    const pos = parseCoordKey(key);
    let dist = manhattanDistance(pos, { col: targetCol, row: targetRow });
    // Soft penalty: prefer tiles outside enemy ZoC when equidistant
    if (zoc.has(key)) dist += 0.5;
    if (dist < bestDist) {
      bestDist = dist;
      bestKey = key;
    }
  }

  if (bestKey) {
    const pos = parseCoordKey(bestKey);
    const path = findPath(world, unit, pos, zoc);
    if (path) {
      _lastPath = path;
      // Subtract movement cost for the path
      const def = UNIT_DEFS[unit.type];
      let cost = 0;
      for (const step of path) {
        const terrain = world.map.tiles[step.row][step.col].type;
        cost += getMovementCost(terrain, def.domain, unit.type);
      }
      unit.col = pos.col;
      unit.row = pos.row;
      unit.movePoints = Math.max(0, unit.movePoints - cost);
      return true;
    }
  }
  return false;
}

function aiRecruit(world: TurnWorld, pid: number): void {
  const player = world.players[pid];
  const factionId = world.factions?.get(pid);
  const buildings = [...world.buildings.values()].filter(
    (b) => b.owner === pid && !b.training,
  );

  // Prioritize: barracks (cheap infantry for capture) > others
  buildings.sort((a, b) => {
    const recruitsA = getRecruitList(a.type, factionId);
    const recruitsB = getRecruitList(b.type, factionId);
    if (recruitsA.length > 0 && recruitsB.length === 0) return -1;
    if (recruitsA.length === 0 && recruitsB.length > 0) return 1;
    if (recruitsA.length > 0 && recruitsB.length > 0) {
      const cheapestA = Math.min(...recruitsA.map(t => UNIT_DEFS[t].cost));
      const cheapestB = Math.min(...recruitsB.map(t => UNIT_DEFS[t].cost));
      return cheapestA - cheapestB;
    }
    return 0;
  });

  for (const b of buildings) {
    const recruits = getRecruitList(b.type, factionId);
    if (recruits.length === 0) continue;
    const unitType = aiChooseRecruitType(world, recruits, pid);
    if (!unitType) continue;
    if (player.gold >= UNIT_DEFS[unitType].cost) {
      startTraining(world, b, unitType);
    }
  }
}

/** Choose which unit type to recruit from a building's options. */
function aiChooseRecruitType(world: TurnWorld, options: import('./types').UnitType[], pid: number): import('./types').UnitType | null {
  // Supply-truck recruitment: build when combat units outnumber trucks 8:1
  if (options.includes('supply-truck')) {
    const combatUnits = [...world.units.values()].filter(
      u => u.owner === pid && u.type !== 'supply-truck' && u.type !== 'medic' && UNIT_DEFS[u.type].attack > 0,
    ).length;
    const trucks = [...world.units.values()].filter(
      u => u.owner === pid && u.type === 'supply-truck',
    ).length;
    if (combatUnits > 0 && (trucks === 0 || combatUnits / trucks > 8)) return 'supply-truck';
  }
  const filtered = options.filter(t => t !== 'supply-truck');
  if (filtered.length === 0) return null;
  if (filtered.length === 1) return filtered[0];

  const hasBase = (base: string) => filtered.some(t => t === base || resolveBaseType(t) === base);
  const findByBase = (base: string) => filtered.find(t => t === base || resolveBaseType(t) === base);

  // Heuristic: recruit recon when AI has low visibility (explored < 30% of map)
  if (hasBase('recon')) {
    const totalTiles = world.map.width * world.map.height;
    const exploredCount = world.explored[pid].size;
    const hasReconUnit = [...world.units.values()].some(u => u.owner === pid && resolveBaseType(u.type) === 'recon');
    if (!hasReconUnit && exploredCount < totalTiles * 0.3) return findByBase('recon')!;
  }

  // For vehicle-factory (tank vs apc): prefer APC if there's idle infantry far from targets
  if (hasBase('apc') && hasBase('medium-tank')) {
    const friendlyInfantry = [...world.units.values()].filter(
      u => u.owner === pid && resolveBaseType(u.type) === 'infantry',
    );
    const friendlyApcs = [...world.units.values()].filter(
      u => u.owner === pid && resolveBaseType(u.type) === 'apc',
    );
    // If we have infantry but no APC, build one
    if (friendlyInfantry.length > 0 && friendlyApcs.length === 0) return findByBase('apc')!;
    // Otherwise default to medium-tank (prefer faction variant if available)
    return findByBase('medium-tank')!;
  }

  // Default: cheapest option
  let cheapest = filtered[0];
  let cheapestCost = Infinity;
  for (const opt of filtered) {
    const cost = UNIT_DEFS[opt].cost;
    if (cost < cheapestCost) {
      cheapestCost = cost;
      cheapest = opt;
    }
  }
  return cheapest;
}
